import { useState } from 'react';
import { usePermissions } from '../contexts/permissionsContext';

/**
 * @hook usePermissionCheck
 * @description Hook para verificar permissões com refresh e dialog de erro
 * @returns {Object} - Objeto com funções de verificação de permissões
 */
const usePermissionCheck = () => {
  const { 
    permissions, 
    hasPermission, 
    hasPermissionAction, 
    getPermission, 
    refreshPermissions, 
    clearCacheAndRefresh,
    isLoading,
    refreshTrigger
  } = usePermissions();

  const [permissionError, setPermissionError] = useState(null);

  /**
   * @function checkPermissionWithRefresh
   * @description Verifica permissão com refresh automático
   * @param {string} permission - Nome da permissão
   * @param {string} action - Ação específica (opcional)
   * @param {boolean} showError - Se deve mostrar erro em caso de falha
   * @returns {Promise<boolean>} - True se tem permissão
   */
  const checkPermissionWithRefresh = async (permission, action = null, showError = true, forceRefresh = false, autoRefresh = false) => {
    console.log('🔍 Iniciando verificação de permissão com refresh');
    try {
      // Só faz refresh se forçado, se as permissões estiverem vazias, ou se autoRefresh estiver ativado
      if (forceRefresh || permissions.length === 0 || autoRefresh) {
        console.log('🔍 Fazendo refresh das permissões');
        const refreshResult = await refreshPermissions();
        
        if (!refreshResult.success) {
          console.error('❌ Erro ao atualizar permissões:', refreshResult.error);
          if (showError) {
            setPermissionError({
              title: 'Erro ao verificar permissões',
              message: 'Não foi possível verificar suas permissões. Tente novamente.',
              type: 'error'
            });
          }
          return false;
        }
      }

      // Verifica a permissão
      const hasPerm = action 
        ? hasPermissionAction(permission, action)
        : hasPermission(permission);

      if (!hasPerm && showError) {
        setPermissionError({
          title: 'Acesso Negado',
          message: `Você não tem permissão para acessar este recurso.`,
          type: 'warning'
        });
      }

      console.log(`${hasPerm ? '✅' : '❌'} Permissão ${permission}${action ? `.${action}` : ''}: ${hasPerm}`);
      return hasPerm;

    } catch (error) {
      console.error('❌ Erro ao verificar permissão:', error);
      if (showError) {
        setPermissionError({
          title: 'Erro de Verificação',
          message: 'Ocorreu um erro ao verificar suas permissões.',
          type: 'error'
        });
      }
      return false;
    }
  };

  /**
   * @function checkMultiplePermissions
   * @description Verifica múltiplas permissões
   * @param {Array} permissionList - Lista de permissões para verificar
   * @param {boolean} showError - Se deve mostrar erro em caso de falha
   * @returns {Promise<boolean>} - True se tem todas as permissões
   */
  const checkMultiplePermissions = async (permissionList, showError = true) => {
    console.log(`🔍 Verificando múltiplas permissões:`, permissionList);
    try {
      
      // Refresh das permissões
      const refreshResult = await refreshPermissions();
      
      if (!refreshResult.success) {
        console.error('❌ Erro ao atualizar permissões:', refreshResult.error);
        if (showError) {
          setPermissionError({
            title: 'Erro ao verificar permissões',
            message: 'Não foi possível verificar suas permissões. Tente novamente.',
            type: 'error'
          });
        }
        return false;
      }

      // Verifica cada permissão
      const results = permissionList.map(({ permission, action }) => {
        const hasPerm = action 
          ? hasPermissionAction(permission, action)
          : hasPermission(permission);
        console.log(`${hasPerm ? '✅' : '❌'} ${permission}${action ? `.${action}` : ''}: ${hasPerm}`);
        return hasPerm;
      });

      const allPermissions = results.every(result => result === true);

      if (!allPermissions && showError) {
        setPermissionError({
          title: 'Acesso Negado',
          message: 'Você não tem todas as permissões necessárias para acessar este recurso.',
          type: 'warning'
        });
      }

      return allPermissions;

    } catch (error) {
      console.error('❌ Erro ao verificar permissões:', error);
      if (showError) {
        setPermissionError({
          title: 'Erro de Verificação',
          message: 'Ocorreu um erro ao verificar suas permissões.',
          type: 'error'
        });
      }
      return false;
    }
  };

  /**
   * @function clearPermissionError
   * @description Limpa o erro de permissão
   */
  const clearPermissionError = () => {
    setPermissionError(null);
  };

  return {
    permissions,
    hasPermission,
    hasPermissionAction,
    getPermission,
    refreshPermissions,
    clearCacheAndRefresh,
    isLoading,
    refreshTrigger,
    checkPermissionWithRefresh,
    checkMultiplePermissions,
    permissionError,
    clearPermissionError
  };
};

export default usePermissionCheck;
